import {WBFormLabel} from '@wandb/ui';
import {PopupDropdown} from '@wandb/weave/common/components/PopupDropdown';
import {Alert} from '@wandb/weave/components/Alert';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import {Checkbox} from '@wandb/weave/components/Checkbox';
import {IconInfo, IconStop} from '@wandb/weave/components/Icon';
import {RunState} from '@wandb/weave/components/RunState';
import {Timestamp} from '@wandb/weave/components/Timestamp';
import {TooltipDeprecated} from '@wandb/weave/components/TooltipDeprecated';
import _ from 'lodash';
import React, {useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import {CellInfo, Column} from 'react-table';
// eslint-disable-next-line wandb/no-deprecated-imports
import {DropdownItemProps} from 'semantic-ui-react';

import {
  useStopRunMutation,
  useUpsertView2Mutation,
} from '../../../generated/graphql';
import {useViewer} from '../../../state/viewer/hooks';
import {useNavigateTo} from '../../../util/history';
import {
  job as jobUrl,
  profilePage,
  project as projectUrl,
  run as runUrl,
} from '../../../util/urls';
import {SemanticLoader as Loader} from '../../utility/InstrumentedLoader';
import {useProjectView} from '../hooks';
import {LinkRun} from '../LinkRun';
import {isWandbJobsProject} from '../publicQueue/utils';
import {SelectJobVersion} from '../SelectJobVersion';
import {Table} from '../Table';
import {JsonObject} from '../utilTypes';
import * as S from './TabRuns.styles';
import {hasJobAlias} from './utils';
import {JobDetails, JobRun, JobVersion} from './utilTypes';

type TabRunsProps = {
  entityName: string;
  projectName: string;
  job: JobDetails;
};

const runMatchesFilter = (run: JobRun, filter: string): boolean => {
  return filter === '*' || hasJobAlias(run, filter);
};

// runs from partial jobs use both the partial job and full job version
// filter the runs to only the newest version
const filterRunsRowsByNewestJobVersion = (runs: JobRun[]): JobRun[] => {
  const runsMap = new Map<string, JobRun>();
  for (const run of runs) {
    // If we've already seen a run with this id, only keep the latest version.
    const existingRun = runsMap.get(run.id);
    if (existingRun != null && existingRun.version > run.version) {
      continue;
    }
    runsMap.set(run.id, run);
  }
  return Array.from(runsMap.values());
};

const userHasAccessToRun = (
  runEntityName: string,
  viewerEntity: string,
  viewerTeams?: string[]
) => {
  return (
    runEntityName === viewerEntity ||
    (viewerTeams != null && viewerTeams.includes(runEntityName))
  );
};

// Get the artifact version ids corresponding to the alias filter.
const getFilteredArtifactIds = (
  versions: JobVersion[],
  filterAlias: string
): string[] => {
  if (filterAlias === '*') {
    return versions.map(version => version.id);
  }
  const foundVersion = versions.filter(v =>
    v.aliases.some(a => a.alias === filterAlias)
  );
  const artifactId = foundVersion[0].id;
  return [artifactId];
};

// Return a cloned version of spec with the filters replaced with a "Using Artifact" filter.
const setSpecFilters = (spec: JsonObject, artifactIds: string[]) => {
  // TODO: Can this type be made more specific?
  const cloned = _.clone(spec) as any;
  cloned.section.runSets[0].filters.filters = [
    {
      op: 'AND',
      filters: [
        {
          key: {
            section: 'run',
            name: 'inputArtifacts',
          },
          op: 'IN',
          disabled: false,
          value: artifactIds,
        },
      ],
    },
  ];
  return cloned;
};

export const TabRuns = ({entityName, projectName, job}: TabRunsProps) => {
  const [hoveredRow, setHoveredRow] = useState(null);
  const [upsertView] = useUpsertView2Mutation();
  const [stopRun] = useStopRunMutation();

  const viewer = useViewer();
  const viewerTeams: string[] = viewer?.teams.edges.map(e => e.node.name) ?? [];
  const viewerEntity = viewer?.username ?? '';
  const inWandbJobs = isWandbJobsProject(entityName, projectName);

  const [showPublicRuns, setShowPublicRuns] = useState(false);

  const {viewId, spec, customRunColors, loading, error} = useProjectView(
    entityName,
    projectName
  );
  const navigateTo = useNavigateTo();
  const [filterAlias, setFilteredAlias] = useState('*');
  const onSelectJobAlias = (jobAlias: string) => {
    setFilteredAlias(jobAlias);
  };

  const runs = useMemo(
    () => filterRunsRowsByNewestJobVersion(job.runs),
    [job.runs]
  );

  if (error) {
    // TODO: Should we ignore the error and proceed with default colors?
    return <Alert severity="error">Error loading custom run colors</Alert>;
  }
  if (loading) {
    return <Loader active name="tab-runs-loader" />;
  }
  if (job.versions.length === 0) {
    return <Alert>There are no versions of this job.</Alert>;
  }

  const rows = runs
    .filter(run => runMatchesFilter(run, filterAlias))
    .filter(
      run =>
        showPublicRuns ||
        userHasAccessToRun(run.entityName, viewerEntity, viewerTeams)
    )
    .map(run => {
      const urlParams = {
        entityName: run.entityName,
        projectName: run.projectName,
        name: run.name,
      };
      const urlRun = runUrl(urlParams);
      const urlProfile = profilePage(run.creator?.username ?? '');
      const urlProject = projectUrl({
        entityName: run.entityName,
        name: run.projectName,
      });
      return {
        // TODO: Should we allow searching for run.name (i.e. its id)?
        //       This can give confusing results with short searches since we don't display the name.
        searchString: run.displayName,
        row: {
          ...run,
          urlRun,
          urlProfile,
          urlProject,
        },
      };
    });

  const columns: Column[] = [
    {
      Header: 'Run',
      accessor: 'displayName',
      resizable: true,
      Cell: (cellInfo: CellInfo) => (
        <LinkRun
          entityName={cellInfo.original.entityName}
          projectName={cellInfo.original.projectName}
          name={cellInfo.original.name}
          displayName={cellInfo.value}
          defaultColor={cellInfo.original.defaultColor}
          customRunColors={customRunColors}
        />
      ),
    },
    {
      Header: 'State',
      accessor: 'state',
      width: 110,
      minWidth: 110,
      // If you resize the column to just the wrong size, it shows
      // the value truncated with ellipsis, obscured just enough to look like a
      // mysterious extra period. Clip fixes that.
      Cell: (cellInfo: CellInfo) => (
        <S.ClipOverflow>
          <RunState value={cellInfo.value} />
        </S.ClipOverflow>
      ),
    },
    {
      Header: 'Job version',
      accessor: 'version',
      width: 100,
      Cell: (cellInfo: CellInfo) => {
        const jobVersion = `v${cellInfo.value}`;
        const urlParams = {
          entityName,
          projectName,
          jobId: job.id,
          jobVersion,
          tab: 'version_details',
        };
        const pathname = jobUrl(urlParams);
        return <Link to={pathname}>{jobVersion}</Link>;
      },
    },
    {
      Header: 'Creation date',
      accessor: 'createdAt',
      Cell: (cellInfo: CellInfo) => {
        return <Timestamp value={cellInfo.value} />;
      },
    },
    {
      Header: 'Creator',
      accessor: 'creator',
      Cell: (cellInfo: CellInfo) => {
        if (cellInfo.value.username) {
          return (
            <Link to={cellInfo.original.urlProfile}>
              {cellInfo.value.username}
            </Link>
          );
        }
        // If run was created by a service account it won't have username
        return cellInfo.value.name;
      },
    },
    {
      ...(inWandbJobs && {
        Header: 'Entity',
        accessor: 'entityName',
      }),
    },
    {
      ...(inWandbJobs && {
        Header: 'Project',
        accessor: 'projectName',
        Cell: (cellInfo: CellInfo) => {
          return (
            <Link to={cellInfo.original.urlProject}>
              {cellInfo.original.projectName}
            </Link>
          );
        },
      }),
    },
  ];

  // Add a column for every config key seen across the runs.
  for (const configKey of job.configKeys) {
    columns.push({
      accessor: `_config_${configKey}`,
      Header: configKey,
      resizable: true,
      Cell: (cellInfo: CellInfo) => {
        const config = cellInfo.original.config;
        const value = config[configKey]?.value;
        if (typeof value === 'object') {
          return <span>{JSON.stringify(value)}</span>;
        }
        return value ?? null;
      },
    });
  }

  columns.push({
    Header: '',
    accessor: 'id',
    width: 60,
    sortable: false,
    style: {
      padding: '4px',
    },
    Cell: (cellInfo: CellInfo) => {
      if (hoveredRow !== cellInfo.value) {
        return null;
      }
      const state = cellInfo.original.state.toLowerCase();
      const showStop = ['running', 'stopping'].includes(state);
      const menuSections: DropdownItemProps[][] = [
        [
          {
            key: 'view-run-details',
            text: 'View run details',
            icon: <IconInfo style={{marginRight: '4px'}} />,
            onClick: () => {
              navigateTo({pathname: cellInfo.original.urlRun});
            },
          },
        ],
      ];
      if (showStop) {
        menuSections.push([
          {
            key: 'stop-run',
            text: 'Stop run',
            icon: <IconStop style={{marginRight: '4px'}} />,
            disabled: state === 'stopping',
            onClick: () => {
              const id = cellInfo.original.id;
              stopRun({variables: {id}});
            },
          },
        ]);
      }
      return (
        <PopupDropdown
          position="bottom right"
          trigger={<Button icon="overflow-horizontal" variant="ghost" />}
          sections={menuSections}
        />
      );
    },
  });

  // User clicks "View in Workspace"
  const onViewRuns = () => {
    const artifactIds = getFilteredArtifactIds(job.versions, filterAlias);
    const updatedSpec = setSpecFilters(spec, artifactIds);
    const newSpec = JSON.stringify(updatedSpec);
    upsertView({variables: {id: viewId, spec: newSpec}});
    const pathname = projectUrl({entityName, name: projectName});
    navigateTo({pathname});
  };

  const defaultSorted = [{id: 'createdAt', desc: true}];

  return (
    <>
      <S.ControlBar>
        <S.SelectJobVersionSize>
          <SelectJobVersion
            jobVersions={job.versions}
            selectedAlias={filterAlias}
            includeAll={true}
            onSelectJobAlias={onSelectJobAlias}
          />
        </S.SelectJobVersionSize>
        <S.ButtonToRight>
          {inWandbJobs && (
            <TooltipDeprecated
              position="top center"
              trigger={
                <WBFormLabel
                  control={
                    <Checkbox
                      style={{marginRight: 8}}
                      checked={showPublicRuns}
                      onCheckedChange={() => {
                        setShowPublicRuns(!showPublicRuns);
                      }}
                    />
                  }
                  label="Include public runs"
                />
              }
              style={{textAlign: 'left'}}>
              Checking this box will include public runs that do not belong to
              your entities.
            </TooltipDeprecated>
          )}
          {!inWandbJobs && (
            <Button
              variant="ghost"
              icon="dashboard-blackboard"
              onClick={onViewRuns}>
              View in workspace
            </Button>
          )}
        </S.ButtonToRight>
      </S.ControlBar>

      <Table
        data={rows}
        columns={columns}
        pageSize={20}
        defaultSorted={defaultSorted}
        getTrProps={(state: any, rowInfo: any) => {
          if (rowInfo && rowInfo.row) {
            return {
              onMouseEnter: (e: React.MouseEvent) => {
                setHoveredRow(rowInfo.original.id);
              },
              onMouseLeave: (e: React.MouseEvent) => {
                setHoveredRow(null);
              },
            };
          } else {
            return {};
          }
        }}
      />
    </>
  );
};
